import { mainItemTypeValues } from 'default-settings';
import { notForPreview } from 'widget/utils/not-for-preview';
import autobind from 'core-decorators/lib/autobind';

import WixSDK from 'shared/utils/wix-sdk';
import paymentsHelper from 'widget/utils/payments';
import { canPlayFullOrPreview } from 'widget/ui-selectors/video-access';
import { getOpenedEventParams } from 'shared/utils/bi/video-overlay-events-params';
import { playerSize } from 'widget/utils/videos-sizes';

import { openFullScreenOverlay } from 'shared/utils/open-overlay';

import events from 'shared/constants/events';
import viewModes from 'shared/constants/view-modes';
import SEARCH_QUERY_FIELDS from 'shared/constants/search-query-fields';

import Spinner from 'shared/components/spinner/loading-spinner';
import Icon from 'shared/components/icon/icon';
import ResizedImage from 'shared/components/resized-image/resized-image';
import { openPurchase, openSubscription } from 'widget/components/payments/payments';


export default class BaseView extends React.Component {

  static propTypes = {
    mainVideoId: React.PropTypes.string,
    isVideoPlaying: React.PropTypes.bool,
    playVideo: React.PropTypes.func,
    pauseVideo: React.PropTypes.func,
    selectVideo: React.PropTypes.func,
    resetSelectedVideo: React.PropTypes.func,
    currentChannelId: React.PropTypes.string,
    canShowChannelCover: React.PropTypes.bool,
    prevVideoId: React.PropTypes.string,
    nextVideoId: React.PropTypes.string,
    channelData: React.PropTypes.object,
    channelVideoData: React.PropTypes.object,
    firstChannelVideoId: React.PropTypes.string,
    videoByIds: React.PropTypes.object,
    channel: React.PropTypes.object,
    currentSiteUser: React.PropTypes.object,
    channelVideo: React.PropTypes.object,
    channelPaymentData: React.PropTypes.object,
    paginatedChannelVideos: React.PropTypes.object,
    appSettings: React.PropTypes.object,
    locale: React.PropTypes.string,
    ui: React.PropTypes.object,
    updateUI: React.PropTypes.func,
    resetUI: React.PropTypes.func,
    biEvent: React.PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.isOpenedBiSent = false;
  }

  componentDidMount() {
    // TODO: move to HOC after moving canShowChannelCover and paused to Redux Store
    WixSDK.addEventListener(WixSDK.Events.SETTINGS_UPDATED, this.reloadOnDashboardClosing);
    WixSDK.addEventListener(WixSDK.Events.PAGE_NAVIGATION, this.stopPlaybackOnPageChange);
    WixSDK.addEventListener(WixSDK.Events.EDIT_MODE_CHANGE, this.reset);
  }

  componentWillReceiveProps(nextProps) {
    if (WixSDK.Utils.getViewMode() === viewModes.EDITOR && this.hasChangesInAppSettings(nextProps)) {
      this.reset(nextProps);
    }
  }

  componentDidUpdate(prevProps) {
    const { currentChannelId } = this.props;

    this.logOpenedBIEvent();

    // TODO: move to HOC after move canShowChannelCover to Redux Store
    if (currentChannelId !== prevProps.currentChannelId) {
      this.reset();
    }
  }

  componentWillUnmount() {
    WixSDK.removeEventListener(WixSDK.Events.SETTINGS_UPDATED, this.reloadOnDashboardClosing);
    WixSDK.removeEventListener(WixSDK.Events.PAGE_NAVIGATION, this.stopPlaybackOnPageChange);
    WixSDK.removeEventListener(WixSDK.Events.EDIT_MODE_CHANGE, this.reset);
  }

  hasChangesInAppSettings(nextProps) {
    const { booleans: nextBooleans, numbers: nextNumbers } = nextProps.appSettings;
    const { booleans: thisBooleans, numbers: thisNumbers } = this.props.appSettings;
    return (
      !_.isEqual(nextBooleans, thisBooleans) || !_.isEqual(nextNumbers, thisNumbers)
    );
  }

  get mainVideoList() {
    return this.props.paginatedChannelVideos;
  }

  get playerSize() {
    return playerSize(this.props.appSettings.requestedWidgetSize.width);
  }

  get mainVideo() {
    const { mainVideoId, videoByIds } = this.props;
    return videoByIds[mainVideoId];
  }

  permitSearch(searchQuery, searchQueryField = SEARCH_QUERY_FIELDS.QUERY) {
    this.props.updateUI({ searchQuery, searchQueryField });
  }

  get isCategorySelected() {
    return this.props.ui.searchQueryField === SEARCH_QUERY_FIELDS.CATEGORY;
  }

  showSearchLayout() {
    this.setState({ searchLayout: true });
  }

  logOpenedBIEvent() {
    const { appSettings, channelData } = this.props;
    const videoItem = this.mainVideo;

    if (!videoItem || this.isOpenedBiSent) {
      return;
    }

    this.isOpenedBiSent = true;

    _.delay(() => {
      this.props.biEvent(
        'widget.opened',
        getOpenedEventParams(appSettings, channelData, videoItem, this.canShowChannelCover),
      );
    });
  }

  storeDataForReload() {
    return null;
  }

  // TODO: move to channel action callbacks after moving storeDataForReload to data-component
  @autobind
  @notForPreview
  showLogInForm() {
    WixSDK.requestLogin().then(this.storeDataForReload);
  }

  // TODO: move to channel action callbacks after moving storeDataForReload to data-component
  @autobind
  logOut() {
    WixSDK.logOutCurrentMember();
    this.storeDataForReload();
  }

  @autobind
  openAccountInfoAtOverlay() {
    const { channelData } = this.props;
    openFullScreenOverlay(`#/account-info/${channelData.id}`);
  }

  @autobind
  clearSearch() {
    const { currentCategory } = this.state;

    if (currentCategory) {
      this.permitSearch(currentCategory, SEARCH_QUERY_FIELDS.CATEGORY);
    } else {
      this.permitSearch('');
    }

    this.setSearchBarValue('');
  }

  @autobind
  searchByQuery(searchQuery) {
    this.permitSearch(searchQuery, SEARCH_QUERY_FIELDS.QUERY);
    this.setState({
      searchBarValue: searchQuery,
      currentCategory: null,
    });
  }

  @autobind
  setSearchBarValue(searchBarValue) {
    this.setState({ searchBarValue });
  }

  @autobind
  searchByCategory(currentCategory) {
    this.permitSearch(currentCategory, SEARCH_QUERY_FIELDS.CATEGORY);
    this.setState({
      currentCategory,
      searchBarValue: '',
    });
  }

  @autobind
  searchByTag(tag) {
    this.setTagsFocusState(false);
    this.permitSearch(tag, SEARCH_QUERY_FIELDS.TAG);
    this.setState({
      searchBarValue: tag,
      currentCategory: null,
    });
    this.sendTagClickedBI();
  }

  @autobind
  handleTagsFocus() {
    this.setTagsFocusState(true);
  }

  @autobind
  handleTagsBlur() {
    this.setTagsFocusState(false);
  }

  setTagsFocusState(isFocused) {
    this.setState({
      isTagFocused: isFocused,
    });

    if (!isFocused) {
      setTimeout(() => this.hideSearchLayout(), 0);
    }
  }

  @autobind
  handleSearchInputFocus() {
    this.setSearchInputFocusState(true);
  }

  @autobind
  handleSearchInputBlur() {
    this.setSearchInputFocusState(false);
  }

  setSearchInputFocusState(isFocused) {
    this.setState({
      isSearchInputFocused: isFocused,
    });

    if (isFocused) {
      this.showSearchLayout();
    } else {
      if (this.hasNoSearchResults) {
        this.clearSearch();
      }
      setTimeout(() => this.hideSearchLayout(), 0);
    }
  }

  get hasNoSearchResults() {
    const { paginatedChannelVideos, ui: { searchQuery } } = this.props;
    return !this.isFetching && searchQuery && !paginatedChannelVideos.ids.length;
  }

  hideSearchLayout() {
    const { isTagFocused, isSearchInputFocused } = this.state;
    if (!isTagFocused && !isSearchInputFocused) {
      this.setState({
        searchLayout: false,
      });
    }
  }

  sendTagClickedBI() {
    this.props.biEvent('widget.tags.clicked');
  }

  @autobind
  openPurchase() {
    openPurchase((this.mainVideo).id);
  }

  @autobind
  openSubscription() {
    openSubscription(this.props.biEvent);
  }

  @autobind
  selectVideo({ id }) {
    this.props.selectVideo(id);
  }

  @autobind
  playSelectedVideo() {
    const { mainVideoId: id } = this.props;

    this.playVideo({ id });
  }

  @autobind
  playPrevVideo() {
    const { prevVideoId: id } = this.props;

    if (id) {
      this.playVideo({ id });
    }
  }

  @autobind
  playNextVideo() {
    const { nextVideoId: id } = this.props;

    if (id) {
      this.playVideo({ id });
    }
  }

  playVideo({ id }) {
    const { videoByIds, channelData, currentSiteUser, selectVideo, playVideo } = this.props;
    const videoItem = videoByIds[id];
    const isPlayAllowed = canPlayFullOrPreview(channelData, videoItem, currentSiteUser.data);

    if (isPlayAllowed) {
      playVideo(videoItem.id);
    } else {
      selectVideo(videoItem.id);
    }
  }

  @autobind
  selectNextVideo() {
    const id = this.props.nextVideoId;

    if (id) {
      this.selectVideo({ id });
    }
  }

  @autobind
  selectPrevVideo() {
    const id = this.props.prevVideoId;

    if (id) {
      this.selectVideo({ id });
    }
  }

  get canShowChannelCover() {
    return this.props.canShowChannelCover;
  }

  get isMainItemChannelCover() {
    return this.props.appSettings.numbers.mainItemType === mainItemTypeValues.CHANNEL_COVER;
  }

  get isChannelCoverShown() {
    return this.canShowChannelCover && this.isMainItemChannelCover;
  }

  // TODO: move to redux store
  // https://jira.wixpress.com/browse/VOD-292
  @autobind
  setFullScreenState(isInFullScreen) {
    // in full screen in mobile the innerWidth becomes bigger that is should be, so exit.
    // Seems like this is viewer issue because site itself becomes wider than 320px.
    this.setState({ isInFullScreen });
  }

  reloadData() {
    const { channel, channelPaymentData } = this.props;

    channel.fetch();
    channelPaymentData.fetch();
    this.mainVideoList.fetch();
  }

  // TODO: rename to cancelSubscription
  @autobind
  processSubscriptionCancel() {
    const { appSettings: { siteUrl, instance }, currentChannelId } = this.props;
    paymentsHelper.cancelSubscription(currentChannelId, siteUrl, instance);
  }

  @autobind
  reset() {
    const { resetUI, resetSelectedVideo } = this.props;

    resetUI();
    resetSelectedVideo();
  }

  @autobind
  reloadOnDashboardClosing(event) {
    if (event === events.DASHBOARD.CLOSED) {
      this.reloadData();
      this.reset();
    }
  }

  @autobind
  setCurrentVideoFromPayment(paymentEvent) {
    if (paymentEvent.itemId) {
      this.selectVideo({
        id: paymentEvent.itemId,
      });
    }
  }

  @autobind
  stopPlaybackOnPageChange({ wasAppOnPage }) {
    if (wasAppOnPage) {
      this.props.pauseVideo();
    }
  }

  get isFetching() {
    const { mainVideoList } = this;
    const { channel } = this.props;
    return channel.isFetching || mainVideoList.isFetching && !mainVideoList.loadedUIPagesCount;
  }

  get noVideoPlayerOverlay() {
    return (
      <div />
    );
  }

  get noVideosPlayerContent() {
    const { channelData } = this.props;
    const { width, height } = this.playerSize;

    if (!this.isMainItemChannelCover) {
      return (
        <div styleName="no-player"
             style={{ width, height }}
             data-hook="noPlayer">
          <Icon styleName="no-player-play"
                name="arrow-right-filled" />
        </div>
      );
    }

    const coverUrl = channelData.customCoverUrl || channelData.coverUrl;
    return (
      <ResizedImage styleName="overlay-only"
                    src={coverUrl}
                    width={width}
                    height={height}>
        {this.noVideoPlayerOverlay}
      </ResizedImage>
    );
  }

  get isUIBlockingDataLoaded() {
    const { channelData, currentSiteUser, channelPaymentData, channelVideo } = this.props;

    return !_.isEmpty(channelData) &&
      !currentSiteUser.isFetching &&
      !channelPaymentData.isFetching &&
      !channelVideo.isFetching;
  }

  get spinner() {
    return (
      <div styleName="spinner-container">
        <Spinner styleName="spinner" />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/_base.js